import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "./api";
import {
  inviteAcceptedResponse,
  magicLinkRequestedResponse,
  meResponse,
  pendingInvitesResponse,
  signOutResponse,
  socialSignInResponse,
  workspaceCreatedResponse,
} from "@sitebrew/contracts/v1";

/*
 * Re-exported for the fleet-copied components (`components/user-management`, `CreateWorkspaceModal`,
 * held byte-for-byte to the archive by `fleet-copy.test.js`), which import
 * their types from here. Nothing is declared here any more.
 */
export type { Membership } from "@sitebrew/contracts/v1";
import { keys } from "./query-keys";
import { signInRetryPath } from "./utils";

/*
 * `Me`, `Membership` and `PendingInvite` are `@sitebrew/contracts/v1`'s now
 * (#289 rows 29, 36 and 38): the copies that lived here were held in step with
 * the API by a comment, and `role` was "whatever `/v1/me` said" rather than
 * the checked union it is there.
 */

/**
 * Who is signed in, or `null`.
 *
 * `401` is an answer, not a failure: it is how the API says "nobody", and the
 * only thing the console does with it is show the sign-in screen. Letting it
 * throw would put the "something went wrong" screen in front of every signed
 * out visitor, and retrying it would do that three times over.
 */
export function useMe() {
  return useQuery({
    queryKey: keys.me(),
    queryFn: async () => {
      try {
        return await api("/me", meResponse);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    retry: (count, err) => !(err instanceof ApiError && err.status === 401) && count < 2,
    staleTime: 30_000,
  });
}

/**
 * Ask for a magic link.
 *
 * The screen may only say "check your inbox" when this resolves. A delivery
 * failure shown as a sent link is the failure the whole mail layer is built
 * around: the login looks healthy and nobody can get in (`sitebrew-console#4`).
 */
export function useRequestMagicLink(callbackURL = "/") {
  return useMutation({
    mutationFn: (email: string) =>
      api("/auth/sign-in/magic-link", magicLinkRequestedResponse, {
        method: "POST",
        /**
         * Where the link lands after the API has set the cookie on this origin.
         *
         * A parameter since the OAuth flow: somebody sent here from
         * `/oauth2/authorize` has to come back to *that* address once they are
         * signed in, or the authorization they started is lost and the client
         * waits for a redirect that never arrives. The API refuses anything but
         * a same-origin path (`safeRedirectPath`), so this cannot become an
         * open redirect by being passed through.
         */
        body: { email, callbackURL },
      }),
  });
}

/**
 * Start a Google sign-in.
 *
 * A **POST**, where this used to be a link. What comes back is Google's
 * authorization address, and the same answer carries the `Set-Cookie` holding
 * the state this round is verified against on the way back — an `href` can
 * produce neither, which is why the button stopped being one.
 *
 * `disableRedirect` because the redirect is not ours to follow. `fetch` cannot
 * perform a top-level navigation; left to redirect, the API would answer with a
 * `Location` this call would either chase — fetching Google's consent page into
 * a script that throws it away — or ignore, and either way the person stays on
 * the sign-in screen. So the address is asked for as a value and the browser is
 * sent there by the screen.
 *
 * `callbackURL` is the same value the magic link gets, so both routes into the
 * console end where the person was going, including an authorization they were
 * pulled out of. `errorCallbackURL` carries that address back here rather than
 * to the API's own error page (`signInRetryPath`).
 */
export function useSignInWithGoogle(callbackURL = "/") {
  return useMutation({
    mutationFn: async () => {
      const answer = await api("/auth/sign-in/social", socialSignInResponse, {
        method: "POST",
        body: {
          provider: "google",
          callbackURL,
          errorCallbackURL: signInRetryPath(callbackURL),
          disableRedirect: true,
        },
      });

      // A 200 without an address is not half a sign-in — there is nowhere to
      // send the browser. Thrown rather than returned so the screen shows the
      // failure it is, instead of sitting on "taking you to Google" forever.
      if (!answer.url) throw new Error("The sign-in service did not answer with an address");

      return answer.url;
    },
  });
}

/**
 * An additional workspace of the caller's own (design note 0001 §6, variant A
 * — a person's own second, third, … workspace: separate projects, separate
 * API tokens per agent, no sharing, no invites — that stays M3).
 *
 * `keys.me()` is invalidated rather than written into directly: `/me` is the
 * one place the membership list is read from (`WorkspaceSwitch`'s own
 * `switchable`, built in `app.tsx`), and a hand-assembled insert here would be
 * a second copy of that shape to keep in step with the API's — the same rule
 * `useCreateToken` follows for `keys.tokens`.
 *
 * Refusals are not reworded here either, for the reason `useCreateToken`'s own
 * comment gives: `workspace_cap_reached` is the API's sentence, and a second
 * version of "you may own at most 5" is a second thing to keep in step with
 * `WORKSPACE_CAP`.
 */
export function useCreateWorkspace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { name: string }) =>
      api("/workspaces", workspaceCreatedResponse, {
        method: "POST",
        body: input,
      }).then((body) => body.workspace),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.me() });
    },
  });
}

/**
 * The signed-in account's own open invites — no workspace in the request,
 * for the reason `keys.myInvites` gives.
 */
export function usePendingInvites() {
  return useQuery({
    queryKey: keys.myInvites(),
    queryFn: () => api("/invites/mine", pendingInvitesResponse).then((body) => body.invites),
  });
}

/**
 * Accept one of the account's own invites by id — the counterpart to
 * `InviteAcceptScreen`'s token-based `/invites/accept` for somebody who
 * never opened the mailed link but is signed in already (`acceptPendingInvite`).
 *
 * Both `keys.me()` (a new membership) and `keys.myInvites()` (one fewer open
 * invite) change as a result, for the same reason `useCreateWorkspace`
 * invalidates `keys.me()` rather than writing into it directly.
 */
export function useAcceptPendingInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inviteId: string) =>
      api(`/invites/mine/${inviteId}/accept`, inviteAcceptedResponse, { method: "POST" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.me() });
      void queryClient.invalidateQueries({ queryKey: keys.myInvites() });
    },
  });
}

/**
 * Sign out, and only then believe it.
 *
 * If the API refuses, the session is still valid and the person is told so.
 * "Signed out" over a live session is the same class of lie as "check your
 * inbox" with nothing sent, and worse on a shared machine.
 */
export function useSignOut() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api("/auth/sign-out", signOutResponse, { method: "POST", body: {} }),
    onSuccess: () => {
      /*
       * Who is signed in: nobody, and said so rather than merely un-cached.
       *
       * This used to be `clear()` followed by an `invalidateQueries` on the
       * same key, which did nothing at all — clearing *removes* the query, so
       * the invalidate had nothing left to match and refetched nothing, and
       * the observers reading `useMe` were left holding the signed-in user
       * with no event coming. That is the whole of the bug that looked like a
       * dead sign-out button (review of #8).
       *
       * Writing the value first is what makes it visible: the observers are
       * still attached to *this* query, so they are notified, and `null` is
       * not a guess — the API has just ended the session.
       */
      queryClient.setQueryData(keys.me(), null);

      /*
       * And then everything else, because the cookie it was fetched under is
       * gone and none of it may be shown to whoever sits down next. Removed
       * around the answer above, not over it: `clear()` would take that with
       * it and put us back where we started.
       */
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== keys.me()[0],
      });
    },
  });
}
